import React from "react";
import axios from "axios";
import getConfig from "next/config";
import axiosRetry from "axios-retry";
import { captureMessage, captureException } from "@sentry/nextjs";
import { Props as VideoData } from "../Widget";
import { Howl } from "howler";

axiosRetry(axios, {
  retryCondition: (error) => {
    return true;
  },
});

const {
  publicRuntimeConfig: { buzzerApiUrl },
} = getConfig();

export type VideoEvent = {
  data: VideoData;
  receipt: string;
  videoId: string;
};

const loadAudio = (url: string, volume?: number): Promise<Howl | null> =>
  new Promise((resolve) => {
    const audio = new Howl({
      src: url.endsWith(".ogg") ? [url, url.replace(/\.ogg$/, ".mp3")] : url,
      volume: volume,
    });

    if (audio.state() === "loaded") {
      resolve(audio);
      return;
    }

    audio.once("load", () => resolve(audio));
    audio.once("loaderror", () => resolve(null));
  });

const useVideos = () => {
  const [visible, setVisible] = React.useState<boolean>(false);

  const [event, setEvent] = React.useState<VideoEvent>();

  const showVideo = React.useCallback((receivedEvent: VideoEvent) => {
    if (window.busy) {
      console.log(`Skipping video ${receivedEvent.videoId} (busy)`);
      return;
    }

    console.log(`Showing video ${receivedEvent.videoId}`);

    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
      window.notificationTimeout = undefined;
    }

    window.busy = true;
    window.notificationEvent = receivedEvent;
    window.notificationTimeout = setTimeout(() => {
      captureMessage("Video Timeout", {
        extra: receivedEvent,
      });

      console.error(`Timeout notification ${receivedEvent.videoId}`);

      window.busy = false;
      window.notificationEvent = undefined;
    }, 30_000);

    setEvent(undefined);

    setTimeout(() => {
      setEvent(receivedEvent);
    }, 100);
  }, []);

  const hideVideo = React.useCallback((delay: number = 2_000) => {
    if (!window.notificationEvent) {
      window.busy = false;
      return;
    }

    const event = window.notificationEvent;

    setTimeout(() => {
      if (!window.notificationEvent) {
        window.busy = false;
        return;
      }

      if (event.videoId !== window.notificationEvent.videoId) {
        // Another video is being shown
        return;
      }

      console.log(`Hiding video ${window.notificationEvent.videoId}`);

      setVisible(false);

      let attempts = 0;

      const confirm = () => {
        axios
          .post<VideoEvent>(
            `${buzzerApiUrl}/videos/${window.notificationEvent.videoId}`,
            {},
            {
              headers: {
                "x-receipt": window.notificationEvent.receipt,
              },
              timeout: 10_000,
            },
          )
          .then((response) => {
            window.busy = false;
            window.notificationEvent = undefined;

            setEvent(undefined);

            if (response.status === 200 && response.data) {
              showVideo(response.data);
            }
          })
          .catch((e) => {
            if (attempts >= 3) {
              window.busy = false;
              window.notificationEvent = undefined;

              setVisible(false);
              setEvent(undefined);

              captureException(e);
            } else {
              attempts++;
              setTimeout(confirm, 1_000);
            }
          });
      };

      confirm();
    }, delay);
  }, []);

  const playVideo = React.useCallback(async () => {
    if (!event) return;

    console.log(`Playing video ${event.videoId}`);

    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
      window.notificationTimeout = undefined;
    }

    try {
      const duration =
        (Number(event.data.data.end) - Number(event.data.data.start)) * 1_000;

      const config = event.data.config;

      if (config.audioEnabled && config.audioUrl) {
        const volume = ((config.audioVolume as number) || 50) / 100;
        const audio = await loadAudio(config.audioUrl as string, volume);

        if (audio) {
          audio.once("playerror", console.error);
          audio.play();
        }
      }

      setVisible(true);

      hideVideo(duration + 5_000);
    } catch (e) {
      console.log(e);
      captureException(e);
      hideVideo(0);
    }
  }, [event]);

  return {
    video: event?.data,
    identifier: event?.videoId,
    visible,
    willDisappear: false,
    showVideo,
    hideVideo,
    playVideo,
  };
};

export default useVideos;
